
export type MaturityOptionProps = {
  label: string;        // e.g., "Nonexistent", "Initial", "Developing"
  description: string;  // bespoke JSON description
  selected: boolean;
  onSelect?: () => void;
  disabled?: boolean;
};

export const MaturityOption = ({ label, description, selected, onSelect, disabled = false }: MaturityOptionProps) => {
  const isRiskScale = ["Low Risk", "Moderate Risk", "High Risk"].includes(label);
  // Triage 4-band scale (Low / Medium / High / Unknown) used in pre-investment triage.
  // Also handles Red-Flag answers (None Known / Historical / Active / Yes / No).
  const triageBand: "low" | "medium" | "high" | "unknown" | null =
    label === "Low" || label === "None Known" || label === "No" ? "low"
    : label === "Medium" || label === "Historical (Resolved)" ? "medium"
    : label === "High" || label === "Active / Unresolved" || label === "Yes" ? "high"
    : label === "Unknown" ? "unknown"
    : null;

  const riskColor = isRiskScale
    ? label === "Low Risk"
      ? "border-green-400"
      : label === "Moderate Risk"
      ? "border-yellow-400"
      : "border-red-400"
    : "";

  const riskBadgeColor = isRiskScale
    ? label === "Low Risk"
      ? "bg-green-50 text-green-700 border-green-400"
      : label === "Moderate Risk"
      ? "bg-yellow-50 text-yellow-700 border-yellow-400"
      : "bg-red-50 text-red-700 border-red-400"
    : "";

  const riskDot =
    label === "Low Risk" ? "🟢" : label === "Moderate Risk" ? "🟡" : label === "High Risk" ? "🔴" : "";

  // Triage band styling
  const triageBorder =
    triageBand === "low" ? "border-green-400"
    : triageBand === "medium" ? "border-yellow-400"
    : triageBand === "high" ? "border-red-400"
    : triageBand === "unknown" ? "border-slate-300"
    : "";

  const triageSelectedBg =
    triageBand === "low" ? "bg-green-50"
    : triageBand === "medium" ? "bg-yellow-50"
    : triageBand === "high" ? "bg-red-50"
    : triageBand === "unknown" ? "bg-slate-50"
    : "";

  const triageBadge =
    triageBand === "low" ? "bg-green-50 text-green-700 border-green-400"
    : triageBand === "medium" ? "bg-yellow-50 text-yellow-800 border-yellow-400"
    : triageBand === "high" ? "bg-red-50 text-red-700 border-red-400"
    : triageBand === "unknown" ? "bg-slate-100 text-slate-700 border-slate-300"
    : "";

  const triageDot =
    triageBand === "low" ? "🟢"
    : triageBand === "medium" ? "🟡"
    : triageBand === "high" ? "🔴"
    : triageBand === "unknown" ? "⚪"
    : "";

  return (
    <div
      onClick={disabled ? undefined : onSelect}
      role="radio"
      aria-checked={selected}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.();
        }
      }}
      className={`border rounded-xl p-4 mb-3 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
        ${disabled ? "cursor-default opacity-70" : "cursor-pointer"}
        ${selected
          ? isRiskScale
            ? `${riskColor} border-2 ${label === "Low Risk" ? "bg-green-50" : label === "Moderate Risk" ? "bg-yellow-50" : "bg-red-50"}`
            : triageBand
              ? `${triageBorder} border-2 ${triageSelectedBg}`
              : "border-blue-500 bg-blue-50"
          : isRiskScale
            ? `${riskColor} bg-white`
            : triageBand
              ? `${triageBorder} bg-white`
              : "border-slate-200 bg-white"}
        ${!disabled && !selected ? "hover:bg-slate-50" : ""}`}
    >
      {/* Badge */}
      <span
        className={`inline-flex items-center rounded-full px-2 py-1 text-xs md:text-sm border select-none mb-2 ${
          isRiskScale
            ? `${riskBadgeColor} ${selected ? "border-2 font-semibold" : "opacity-90"}`
            : triageBand
              ? `${triageBadge} ${selected ? "border-2 font-semibold" : "opacity-90"}`
              : selected
              ? "bg-blue-100 text-blue-700 border-blue-500 border-2 font-semibold"
              : "bg-slate-100 text-slate-600 border-slate-200 opacity-90"
        }`}
      >
        {isRiskScale && <span className="mr-1.5">{riskDot}</span>}
        {triageBand && <span className="mr-1.5">{triageDot}</span>}
        {label}
      </span>

      {/* Description */}
      <p className="text-slate-800 text-sm leading-relaxed">{description}</p>
    </div>
  );
};
